import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { getExplorer, getGeneratedImageUrl, deleteDesign, toggleLike, incrementView } from '../services/api';
import { useLang } from '../i18n/LanguageContext';
import { useAuth } from '../auth/AuthContext';

const FILTER_KEYS = [
  { key: 'random',  icon: 'shuffle',    labelKey: 'filterRandom' },
  { key: 'latest',  icon: 'schedule',   labelKey: 'filterLatest' },
  { key: 'likes',   icon: 'favorite',   labelKey: 'filterMostLiked' },
  { key: 'views',   icon: 'visibility', labelKey: 'filterMostViewed' },
];

function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function sortDesigns(data, filter) {
  switch (filter) {
    case 'latest': return [...data].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    case 'likes':  return [...data].sort((a, b) => (b.likeCount || 0) - (a.likeCount || 0));
    case 'views':  return [...data].sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0));
    case 'random':
    default:       return shuffleArray(data);
  }
}

export default function ExplorerPage() {
  const { t } = useLang();
  const { user, isAdmin } = useAuth();
  const [rawDesigns, setRawDesigns] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('random');

  useEffect(() => { loadDesigns(); }, []);

  const loadDesigns = async () => {
    try {
      const { data } = await getExplorer();
      setRawDesigns(data);
      setDesigns(sortDesigns(data, 'random'));
    } catch (err) {
      setError(t.loadFailed);
    }
    setLoading(false);
  };

  const handleFilterChange = (key) => {
    setFilter(key);
    setDesigns(sortDesigns(rawDesigns, key));
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm(t.deleteConfirmOutfit)) return;
    try {
      await deleteDesign(id);
      setRawDesigns(prev => prev.filter(d => d.id !== id));
      setDesigns(prev => prev.filter(d => d.id !== id));
      if (selected?.id === id) setSelected(null);
    } catch (err) {
      setError(t.deleteFailed);
    }
  };

  const handleLike = async (e, id) => {
    e.stopPropagation();
    if (!user) return;
    try {
      const { data } = await toggleLike(id);
      const updater = prev => prev.map(d =>
        d.id === id ? { ...d, likeCount: data.likeCount, isLikedByMe: data.liked } : d
      );
      setRawDesigns(updater);
      setDesigns(updater);
      if (selected?.id === id) {
        setSelected(prev => ({ ...prev, likeCount: data.likeCount, isLikedByMe: data.liked }));
      }
    } catch (err) {
      // ignore
    }
  };

  const handleSelect = async (d) => {
    setSelected(d);
    try {
      const { data } = await incrementView(d.id);
      const updater = prev => prev.map(item =>
        item.id === d.id ? { ...item, viewCount: data.viewCount } : item
      );
      setRawDesigns(updater);
      setDesigns(updater);
      setSelected(prev => prev ? { ...prev, viewCount: data.viewCount } : prev);
    } catch (err) {
      // ignore
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) setSelected(null);
  };

  const handleDownload = (e, d) => {
    e.stopPropagation();
    window.open(getGeneratedImageUrl(d.imageFileName) + '?download=true', '_blank');
  };

  const selectedIdx = selected ? designs.findIndex(d => d.id === selected.id) : -1;

  const goNext = () => {
    if (selectedIdx < designs.length - 1) handleSelect(designs[selectedIdx + 1]);
  };
  const goPrev = () => {
    if (selectedIdx > 0) handleSelect(designs[selectedIdx - 1]);
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (!selected) return;
      if (e.key === 'Escape') setSelected(null);
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  });

  const isOwner = (d) => user && user.id === d.userId;

  if (loading) return (
    <div className="max-w-[1100px] w-full mx-auto px-6 py-8 pb-12">
      <div className="bg-surface rounded-xl p-16 shadow-soft border border-border text-center mt-4">
        <div className="w-12 h-12 border-4 border-gray-200 border-t-[#FA6868] rounded-full mx-auto animate-spin"></div>
        <p className="text-[#64748b] mt-5 text-sm">{t.loadingImages}</p>
      </div>
    </div>
  );

  return (
    <div className="max-w-[1100px] w-full mx-auto px-6 py-8 pb-12 space-y-8">
      {/* Filter Bar */}
      <div className="flex items-center gap-2 flex-wrap justify-end">
        {FILTER_KEYS.map(f => (
          <button
            key={f.key}
            onClick={() => handleFilterChange(f.key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer ${
              filter === f.key
                ? 'bg-[#FA6868] text-white shadow-sm'
                : 'bg-white text-[#64748b] border border-border hover:border-gray-300 hover:text-[#FA6868]'
            }`}
          >
            <span className="material-symbols-outlined text-[18px]">{f.icon}</span>
            {t[f.labelKey]}
          </button>
        ))}
      </div>

      {error && (
        <div className="px-4 py-3 bg-red-50 text-red-700 rounded-lg text-sm border border-red-100">{error}</div>
      )}

      {/* Empty State */}
      {designs.length === 0 && !error && (
        <div className="bg-surface rounded-xl p-16 shadow-soft border border-border text-center">
          <span className="material-symbols-outlined text-5xl text-gray-200 block mb-3">collections</span>
          <p className="text-[#64748b] text-base">{t.noImages}</p>
        </div>
      )}

      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {designs.map((d) => (
          <div
            key={d.id}
            className="group bg-surface rounded-xl overflow-hidden shadow-soft border border-border hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer"
          >
            <div
              className="w-full bg-gray-50 overflow-hidden"
              style={{ height: 450 }}
              onClick={() => handleSelect(d)}
            >
              <img
                src={getGeneratedImageUrl(d.imageFileName)}
                alt={`Outfit #${d.id}`}
                className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-300"
                loading="lazy"
              />
            </div>
            <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-surface">
              <div className="flex items-center gap-3">
                {/* Like button */}
                <button
                  className={`flex items-center gap-1 text-sm transition-colors duration-200 p-1.5 rounded-md ${
                    d.isLikedByMe
                      ? 'text-[#FA6868]'
                      : 'text-[#64748b] hover:text-[#FA6868]'
                  } ${user ? 'cursor-pointer hover:bg-red-50' : 'cursor-default'}`}
                  onClick={(e) => handleLike(e, d.id)}
                  title={user ? (d.isLikedByMe ? 'Unlike' : 'Like') : 'Login to like'}
                >
                  <span className="material-symbols-outlined text-[18px]" style={{ fontVariationSettings: d.isLikedByMe ? "'FILL' 1" : "'FILL' 0" }}>
                    favorite
                  </span>
                  <span className="text-xs font-medium">{d.likeCount || 0}</span>
                </button>
                {/* View count */}
                <span className="flex items-center gap-1 text-[#64748b] text-sm" title="Views">
                  <span className="material-symbols-outlined text-[18px]">visibility</span>
                  <span className="text-xs font-medium">{d.viewCount || 0}</span>
                </span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  className="text-[#64748b] hover:text-[#FA6868] transition-colors duration-200 p-1.5 rounded-md hover:bg-red-50 cursor-pointer"
                  onClick={(e) => handleDownload(e, d)}
                  title="Download"
                >
                  <span className="material-symbols-outlined text-[18px]">download</span>
                </button>
                {(isAdmin || isOwner(d)) && (
                  <button
                    className="text-[#64748b] hover:text-red-500 transition-colors duration-200 text-base p-1.5 rounded-md hover:bg-red-50 cursor-pointer"
                    onClick={(e) => handleDelete(e, d.id)}
                    title="Delete"
                  >
                    <span className="material-symbols-outlined text-[18px]">delete</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {selected && ReactDOM.createPortal(
        <div
          className="fixed inset-0 modal-overlay backdrop-blur-sm flex items-center justify-center z-[9999]"
          onClick={handleOverlayClick}
          style={{ animation: 'fadeIn 0.2s ease' }}
        >
          {/* Close */}
          <button
            className="absolute top-6 right-6 z-10 w-10 h-10 rounded-full modal-btn-icon flex items-center justify-center transition-colors cursor-pointer"
            onClick={() => setSelected(null)}
          >
            <span className="material-symbols-outlined text-[22px]">close</span>
          </button>

          {/* Counter */}
          <div className="absolute top-6 left-6 z-10 px-4 py-2 rounded-lg modal-counter text-xs font-medium">
            {selectedIdx + 1} / {designs.length}
          </div>

          {/* Prev arrow */}
          {selectedIdx > 0 && (
            <button
              className="absolute left-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full modal-btn-icon flex items-center justify-center transition-colors cursor-pointer"
              onClick={(e) => { e.stopPropagation(); goPrev(); }}
            >
              <span className="material-symbols-outlined text-[26px]">chevron_left</span>
            </button>
          )}

          {/* Next arrow */}
          {selectedIdx < designs.length - 1 && (
            <button
              className="absolute right-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full modal-btn-icon flex items-center justify-center transition-colors cursor-pointer"
              onClick={(e) => { e.stopPropagation(); goNext(); }}
            >
              <span className="material-symbols-outlined text-[26px]">chevron_right</span>
            </button>
          )}

          {/* Image + bottom bar */}
          <div className="flex flex-col items-center max-w-[90vw] max-h-[95vh] modal-card-shadow" onClick={(e) => e.stopPropagation()}>
            <img
              src={getGeneratedImageUrl(selected.imageFileName)}
              alt={`Outfit #${selected.id}`}
              className="block max-w-full max-h-[85vh] rounded-t-xl object-contain modal-img-bg"
            />
            {/* Stats bar */}
            <div className="w-full flex items-center justify-between px-6 py-4 modal-stats-bar rounded-b-xl">
              <div className="flex items-center gap-6">
                <button
                  className={`flex items-center gap-2 text-sm transition-colors duration-200 px-3 py-1.5 rounded-lg ${
                    selected.isLikedByMe
                      ? 'text-[#FA6868] bg-[#FA6868]/10'
                      : 'modal-stats-text hover:text-[#FA6868] hover:bg-white/10'
                  } ${user ? 'cursor-pointer' : 'cursor-default'}`}
                  onClick={(e) => handleLike(e, selected.id)}
                >
                  <span className="material-symbols-outlined text-[20px]" style={{ fontVariationSettings: selected.isLikedByMe ? "'FILL' 1" : "'FILL' 0" }}>
                    favorite
                  </span>
                  <span className="font-semibold">{selected.likeCount || 0}</span>
                </button>
                <span className="flex items-center gap-2 modal-stats-text text-sm px-3 py-1.5">
                  <span className="material-symbols-outlined text-[20px]">visibility</span>
                  <span className="font-semibold">{selected.viewCount || 0}</span>
                </span>
              </div>
              <span className="modal-stats-date text-xs font-medium">{new Date(selected.createdAt).toLocaleDateString()}</span>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}